import React from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

// Rapor sayfalarının (Salomon / adidas / ASICS) PDF dışa aktarımı. Sayfadaki görünüm neyse o basılır
// (maliyet override'ı dahil). data-pdf-hide → PDF'te gizli (düğmeler, tarih seçici),
// data-pdf-only → yalnız PDF'te görünür (logolu başlık bandı, KPI şeridi, alt bilgi). PDF_THEME yalnız
// klona uygulanır, ekrandaki sayfa değişmez. Sayfa sonları satır/başlık sınırına denk getirilir.

const NAVY = '#0b1430';
const BLUE = '#1d4ed8';

const PDF_THEME = `
  img { display: inline-block; }
  [data-pdf-root] { background: #ffffff; padding: 0 0 8px; }
  [data-pdf-root] .rounded-3xl { border: none !important; box-shadow: none !important; padding: 4px 28px 0 !important; }
  [data-pdf-root] h2 { font-size: 19px !important; color: ${NAVY} !important; border-left: 5px solid ${BLUE}; padding-left: 12px; margin-top: 8px; }
  [data-pdf-root] h3 { color: ${NAVY} !important; }
  [data-pdf-root] table { font-size: 14px; }
  [data-pdf-root] thead tr { background: ${NAVY} !important; }
  [data-pdf-root] thead th { color: #ffffff !important; }
  [data-pdf-root] tfoot tr { background: #e8eefc !important; color: ${NAVY} !important; }
  [data-pdf-root] .rounded-xl.border { border-color: #d6def0 !important; }
`;

const A4_W = 210;
const A4_H = 297;
const MARGIN = 10;
const SCALE = 2;

function breakPoints(root) {
  const top = root.getBoundingClientRect().top;
  const pts = new Set();
  root.querySelectorAll('tr, h2, h3, [data-pdf-block]').forEach((el) => {
    const r = el.getBoundingClientRect();
    pts.add(Math.round(r.top - top));
    pts.add(Math.round(r.bottom - top));
  });
  return [...pts].sort((a, b) => a - b);
}

export async function exportReportPdf(root, filename) {
  if (!root) return;
  root.setAttribute('data-pdf-root', '');
  // Kırma noktaları klondan ölçülür: klonda kapak/KPI görünür, başlık gizli, tema uygulanmış.
  let breaks = [];
  let rootW = root.getBoundingClientRect().width;
  const canvas = await html2canvas(root, {
    scale: SCALE,
    useCORS: true,
    backgroundColor: '#ffffff',
    ignoreElements: (el) => el.dataset && el.dataset.pdfHide !== undefined,
    onclone: (doc) => {
      doc.querySelectorAll('[data-pdf-only]').forEach((el) => { el.style.display = 'block'; });
      // img{display:inline-block}: Tailwind preflight'taki img{display:block} html2canvas'ta metni kaydırıyor.
      const st = doc.createElement('style');
      st.textContent = PDF_THEME;
      doc.head.appendChild(st);
      const clone = doc.querySelector('[data-pdf-root]');
      if (clone) { breaks = breakPoints(clone); rootW = clone.getBoundingClientRect().width; }
    },
  });

  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const contentW = A4_W - MARGIN * 2;
  const pxPerMm = canvas.width / contentW;
  const pageHpx = Math.floor((A4_H - MARGIN * 2) * pxPerMm);
  const cssToPx = canvas.width / rootW;

  let y = 0;
  let first = true;
  while (y < canvas.height - 1) {
    let end = Math.min(y + pageHpx, canvas.height);
    if (end < canvas.height) {
      // sayfaya sığan en alttaki satır/başlık sınırı
      const candidates = breaks.map((b) => Math.round(b * cssToPx)).filter((b) => b > y + pageHpx * 0.5 && b <= end);
      if (candidates.length) end = candidates[candidates.length - 1];
    }
    const slice = document.createElement('canvas');
    slice.width = canvas.width;
    slice.height = end - y;
    slice.getContext('2d').drawImage(canvas, 0, y, canvas.width, end - y, 0, 0, canvas.width, end - y);
    if (!first) pdf.addPage();
    pdf.addImage(slice.toDataURL('image/jpeg', 0.92), 'JPEG', MARGIN, MARGIN, contentW, (end - y) / pxPerMm);
    first = false;
    y = end;
  }
  pdf.save(filename);
}

const pill = { background: '#ffffff', borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' };

// PDF kapağı: Wunder × marka logosu, başlık, dönem.
export function PdfHeader({ brandLogo, brandName, title, subtitle, startDate, endDate }) {
  const fmt = (d) => d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
  return (
    <div data-pdf-only data-pdf-block style={{ display: 'none', marginBottom: 22 }}>
      <div style={{ background: `linear-gradient(120deg, ${NAVY} 0%, #15255e 55%, ${BLUE} 100%)`, borderRadius: 22, padding: '30px 34px', color: '#fff' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ ...pill, height: 64, padding: '0 22px' }}><img src="/wunderlogo.webp" alt="Wunder" style={{ height: 24 }} /></div>
            <div style={{ fontSize: 22, opacity: 0.7, fontWeight: 300 }}>×</div>
            <div style={{ ...pill, height: 64, padding: '0 18px' }}><img src={brandLogo} alt={brandName} style={{ height: 44, maxWidth: 140, objectFit: 'contain' }} /></div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 11, letterSpacing: 2, opacity: 0.65, fontWeight: 600 }}>DÖNEM</div>
            <div style={{ fontSize: 17, fontWeight: 600, marginTop: 2 }}>{fmt(startDate)} – {fmt(endDate)}</div>
          </div>
        </div>
        <div style={{ marginTop: 26, fontSize: 32, fontWeight: 700, letterSpacing: -0.5 }}>{title}</div>
        {subtitle && <div style={{ marginTop: 6, fontSize: 15, opacity: 0.75 }}>{subtitle}</div>}
      </div>
    </div>
  );
}

// PDF'e özel büyük rakam şeridi.
export function PdfKpis({ items }) {
  return (
    <div data-pdf-only data-pdf-block style={{ display: 'none', marginBottom: 20 }}>
      <div style={{ display: 'flex', gap: 12 }}>
        {items.map((k) => (
          <div key={k.label} style={{ flex: 1, borderRadius: 16, padding: '16px 18px', background: k.accent ? NAVY : '#f3f6fd', color: k.accent ? '#fff' : NAVY }}>
            <div style={{ fontSize: 11, letterSpacing: 1.2, fontWeight: 600, opacity: 0.7 }}>{k.label.toLocaleUpperCase('tr-TR')}</div>
            <div style={{ fontSize: 26, fontWeight: 700, marginTop: 4 }}>{k.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function PdfFooter() {
  return (
    <div data-pdf-only data-pdf-block style={{ display: 'none', margin: '18px 28px 0', paddingTop: 12, borderTop: '1px solid #d6def0', fontSize: 11, color: '#64748b' }}>
      Hazırlayan: Glowy · {new Date().toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
    </div>
  );
}
